import { reverse, sortBy } from 'lodash';
import { SharetribeApi, ShopifyApi } from './api';
import { ListingState, StatusCode } from './enums';
import { isArrayLength } from './genericHelpers';

const stateMap = new Map([
  [ListingState.DRAFT, 'draftListings'],
  [ListingState.PUBLISHED, 'publishedListings'],
  [ListingState.CLOSED, 'closedListings'],
]);

export const categorizeListings = listings => {
  const empty = {
    draftListings: [],
    publishedListings: [],
    soldListings: [],
    closedListings: [],
  };

  if (!isArrayLength(listings)) return empty;

  return listings.reduce((acc, listing) => {
    const state = listing?.attributes?.state;
    const isDeleted = !!listing?.attributes?.deleted;

    if (isDeleted) return acc;

    const stockQty = listing?.currentStock?.attributes?.quantity;

    const isSold = state === 'published' && typeof stockQty === 'number' && stockQty === 0;

    if (isSold) {
      acc.soldListings.push(listing);
      return acc;
    }

    if (state === 'draft') acc.draftListings.push(listing);
    else if (state === 'published') acc.publishedListings.push(listing);
    else if (state === 'closed') acc.closedListings.push(listing);

    return acc;
  }, empty);
};

// Function to set activeTab in sessionStorage
export const setActiveTab = tabValue => {
  if (typeof window === 'undefined') return false;
  if (!tabValue) return false;
  window.sessionStorage.setItem('activeTab', tabValue);
  return true;
};

// Function to get activeTab from sessionStorage
export const getActiveTab = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem('activeTab');
};

// Function to delete activeTab from sessionStorage
export const deleteActiveTab = () => {
  if (typeof window === 'undefined') return false;
  window.sessionStorage.removeItem('activeTab');
  return true;
};

/**
 * Updates a Shopify product with new title, description, and price.
 * @param {Object} params - Update parameters.
 * @param {string} params.shop - The Shopify shop domain.
 * @param {string} params.productId - The Shopify product ID.
 * @param {string} params.userId - The user ID for access token lookup.
 * @param {string} [params.title] - New product title (optional).
 * @param {string} [params.description] - New product description (optional).
 * @param {Object} [params.price] - New price object { amount, currency } (optional).
 * @returns {Promise<{ success: boolean, error?: string }>} Result object.
 */
export const updateShopifyProduct = async params => {
  const { shop, productId, userId, title, description, price } = params || {};

  // Basic validation
  if (!shop || !productId || !userId) {
    const errorMsg = 'Missing required params: shop, productId, or userId';
    console.error(`[Shopify Update] ${errorMsg}`, params);
    return { success: false, error: errorMsg };
  }

  try {
    const response = await ShopifyApi.updateShopifyProductApi({
      shop,
      productId,
      userId,
      title,
      description,
      price,
    });
    if (response.statusCode === StatusCode.SUCCESS) {
      return { success: response.success };
    }
  } catch (error) {
    const errorMsg = `Failed to update Shopify product ${productId}: ${error.message}`;
    return { success: false, error: errorMsg };
  }
};

export const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

export const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

export const fetchUserShippingAddress = async userId => {
  try {
    const response = await SharetribeApi.fetchUserShippingAddress({ userId });
    const { data, statusCode } = response || {};
    if (statusCode === StatusCode.SUCCESS) {
      return data;
    }
  } catch (error) {}
};

export const scrollToMessage = messageId => {
  const selector = `#msg-${messageId.uuid}`;
  const el = document.querySelector(selector);
  if (el) {
    el.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }
};

export const onSendMessageFormFocus = isSafMobile => {
  if (isSafMobile) {
    typeof window !== 'undefined' &&
      window.scroll({ top: document.body.scrollHeight, left: 0, behavior: 'smooth' });
  }
};

// Generates a random 6-digit number
const generateRandomNumber = () => Math.floor(100000 + Math.random() * 900000);

// Checks username availability using SharetribeApi
export const checkUsernameAvailability = async username => {
  try {
    const response = await SharetribeApi.checkUsernameAvailability({ username });
    // Assuming StatusCode.SUCCESS is a constant like 200 or 204
    return response.statusCode === StatusCode.SUCCESS;
  } catch (error) {
    return false; // Treat errors as unavailable to be safe
  }
};

// Generates a unique RBMSeller_XXXXXX username
export const generateUniqueSellerUsername = async () => {
  let attempts = 0;
  const maxAttempts = 10; // Safety limit to prevent infinite loop

  while (attempts < maxAttempts) {
    const randomNum = generateRandomNumber();
    const username = `RBMSeller_${randomNum}`;

    const isAvailable = await checkUsernameAvailability(username);

    if (isAvailable) {
      return username;
    }

    attempts++;
  }

  throw new Error('Could not generate a unique username after multiple attempts');
};

export const formatShoeSize = size => {
  if (typeof size !== "string") return size;

  const match = size.trim().match(/^uk(\d+)$/i);
  if (!match) return size;

  let value = match[1];

  if (value.length > 1 && value.endsWith("5")) {
    value = `${value.slice(0, -1)}.5`;
  }

  return `UK ${value}`;
};
